import { useMutation } from '@tanstack/react-query';
import { UpdateUserTotpStatusRequest } from 'compliance-api';
import { UpdateUserRequest } from 'identity-center-api';
import { useAuth } from 'react-oidc-context';
import { api, identityCenterApi } from '@/modules/common/api/index.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import { COMPLIANCE_CURRENT_USER_QUERY_KEY } from '@/modules/auth/queries/user.tsx';

export const useUpdateUserIdentityCenterProfile = ({
  showToast = true,
}: {
  showToast?: boolean;
} = {}) => {
  const auth = useAuth();

  return useMutation({
    mutationFn: async (payload: UpdateUserRequest) => {
      const response = showToast
        ? await withErrorHandling(identityCenterApi.v1.updateUserV1UsersPut(payload), {
            loading: 'Updating settings...',
            success: 'Settings updated!',
          })
        : await identityCenterApi.v1.updateUserV1UsersPut(payload);

      return response.data;
    },
    onSuccess: async () => {
      await auth.signinSilent();
    },
  });
};

export const useReset2FA = ({ onSuccess }: { onSuccess?: () => void } = {}) => {
  return useMutation({
    mutationFn: async () => {
      const response = await withErrorHandling(api.v1.resetUser2FaV1UsersReset2FaPost(), {
        loading: '2FA is resetting...',
        success: '',
      });

      return response.data;
    },
    onSuccess: () => {
      onSuccess && onSuccess();
      queryClient.invalidateQueries({ queryKey: [COMPLIANCE_CURRENT_USER_QUERY_KEY] });
    },
  });
};

export const useToggle2FA = ({ onSuccess }: { onSuccess?: (enabled: boolean) => void } = {}) => {
  return useMutation({
    mutationFn: async (payload: UpdateUserTotpStatusRequest) => {
      await withErrorHandling(api.v1.updateUserTotpStatusV1UsersOtpPost(payload), {
        loading: 'Updating 2FA...',
        success: `2FA is ${payload.enabled ? 'on' : 'off'}`,
      });

      return payload.enabled;
    },
    onSuccess: enabled => {
      onSuccess && onSuccess(enabled);
      queryClient.invalidateQueries({ queryKey: [COMPLIANCE_CURRENT_USER_QUERY_KEY] });
    },
  });
};
